import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { qrApi, type QrSessione } from "@/api/qr";
import { QrIngresso } from "./QrIngresso";
import { QrInformativa } from "./QrInformativa";
import { QrFoto } from "./QrFoto";
import { QrChat } from "./QrChat";
import { CaricatoreQr } from "./caricatore";

type Passo = "ingresso" | "informativa" | "bolletta" | "documento" | "chat";

function chiaveSessione(token: string) { return `qr:${token}:sessione`; }
// I5: chiave legata alla SESSIONE, non al token — un token (l'agenzia) copre più
// pratiche di seguito nella stessa scheda (il cliente finisce o abbandona una
// pratica e ne inizia un'altra col cartello ancora aperto): una sessione nuova ha
// sempre un session_id nuovo, la chiave riparte da vuota da sola.
function chiaveBatch(sessionId: string) { return `qr:sessione:${sessionId}:batch_inviato`; }

// sessionStorage può lanciare (private browsing, quota, contesti embed): il
// percorso funziona comunque, semplicemente ripartendo da capo a un reload.
function leggiSessione(token: string): QrSessione | null {
  try {
    const raw = sessionStorage.getItem(chiaveSessione(token));
    return raw ? (JSON.parse(raw) as QrSessione) : null;
  } catch {
    return null;
  }
}
function scriviSessione(token: string, s: QrSessione) {
  try { sessionStorage.setItem(chiaveSessione(token), JSON.stringify(s)); } catch { /* ignore */ }
}

function leggiBatchInviato(sessionId: string | null): boolean {
  if (!sessionId) return false;
  try { return sessionStorage.getItem(chiaveBatch(sessionId)) === "1"; } catch { return false; }
}
function scriviBatchInviato(sessionId: string) {
  try { sessionStorage.setItem(chiaveBatch(sessionId), "1"); } catch { /* ignore */ }
}

/** Percorso pubblico del cliente da QR (`/q/:token`, spec §3.1, §5.4). Il passo
 *  corrente vive nell'URL (`?passo=`) così il tasto indietro del telefono torna al
 *  passo precedente (Review Focus 3); sessione in sessionStorage per token, batch per
 *  sessione (I5). Le foto salgono appena scelte (caricatore, 18/10): il caricatore vive
 *  qui per tutta la visita, così un rimontaggio della chat non rimanda nulla. */
export function QrPercorso() {
  const { token = "" } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const passo = (searchParams.get("passo") as Passo | null) ?? "ingresso";

  const [stato, setStato] = useState<"caricamento" | "pronto" | "errore">("caricamento");
  const [agenzia, setAgenzia] = useState("");
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");
  const [sessione, setSessione] = useState<QrSessione | null>(() => leggiSessione(token));
  const [batchInviato, setBatchInviato] = useState(() => leggiBatchInviato(sessione?.session_id ?? null));
  const [bollettaFiles, setBollettaFiles] = useState<File[]>([]);
  const [fronte, setFronte] = useState<File | null>(null);
  const [retro, setRetro] = useState<File | null>(null);
  // un caricatore per sessione: una pratica nuova nella stessa scheda (I5) riparte da zero
  const caricatore = useMemo(
    () => (sessione ? new CaricatoreQr(token, sessione.session_id) : null),
    [token, sessione?.session_id], // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => {
    caricatore?.imposta(bollettaFiles, [fronte, retro].filter((f): f is File => f !== null));
  }, [caricatore, bollettaFiles, fronte, retro]);

  useEffect(() => {
    let cancelled = false;
    qrApi.agenzia(token)
      .then((r) => {
        if (cancelled) return;
        setAgenzia(r.agenzia);
        setTurnstileSiteKey(r.turnstile_site_key);
        setStato("pronto");
      })
      .catch(() => {
        if (!cancelled) setStato("errore");
      });
    return () => { cancelled = true; };
  }, [token]);

  const vai = useCallback((p: Passo) => {
    setSearchParams({ passo: p });
  }, [setSearchParams]);

  function onSessione(s: QrSessione) {
    setSessione(s);
    scriviSessione(token, s);
    // I5: sessione nuova → il batch riparte da zero (una sessione precedente nella
    // stessa scheda non deve far sembrare "già mandato").
    setBatchInviato(leggiBatchInviato(s.session_id));
    vai("bolletta");
  }

  function onBatchInviato() {
    if (!sessione) return;
    setBatchInviato(true);
    scriviBatchInviato(sessione.session_id);
  }

  if (stato === "caricamento") {
    return <div className="min-h-dvh bg-surface" />;
  }
  if (stato === "errore") {
    return (
      <div className="min-h-dvh bg-surface flex items-center justify-center p-6 text-center text-lg">
        Questo codice non è attivo. Chieda al banco.
      </div>
    );
  }

  switch (passo) {
    case "informativa":
      return (
        <QrInformativa
          token={token}
          agenzia={agenzia}
          turnstileSiteKey={turnstileSiteKey}
          onSessione={onSessione}
        />
      );
    case "bolletta":
      return (
        <QrFoto
          variante="bolletta"
          agenzia={agenzia}
          files={bollettaFiles}
          onChange={setBollettaFiles}
          onAvanti={() => { caricatore?.confermaBolletta(); vai("documento"); }}
        />
      );
    case "documento":
      return (
        <QrFoto
          variante="documento"
          agenzia={agenzia}
          fronte={fronte}
          retro={retro}
          onFronte={setFronte}
          onRetro={setRetro}
          onAvanti={() => vai("chat")}
        />
      );
    case "chat":
      // Guardia difensiva: il passo "chat" richiede una sessione già creata
      // all'Informativa. Non raggiungibile navigando dal wizard, solo con un URL
      // scritto a mano senza aver mai fatto l'Informativa.
      if (!sessione || !caricatore) return null;
      return (
        <QrChat
          token={token}
          agenzia={agenzia}
          sessione={sessione}
          caricatore={caricatore}
          batchInviato={batchInviato}
          onBatchInviato={onBatchInviato}
        />
      );
    case "ingresso":
    default:
      return <QrIngresso agenzia={agenzia} onAvanti={() => vai("informativa")} />;
  }
}
